/**
 * Loading Response Placeholder Component
 * Shows the loading state for a new message being processed
 */

import React from 'react';
import { ChatMessage } from '@/types/chatTypes';
import { ModeChip, TabItem } from '@/constants/chatScreenData';
import { Chip, Tab } from '@/design-system';
import {
  spacing,
  strokeColors,
  typographyColors,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { ChatLoadingPlaceholder } from '../ChatLoadingPlaceholder';
import { AudioPlayer } from '../../AudioPlayer';
import { ExpandableText } from '@/components/common/ExpandableText';

export interface LoadingResponsePlaceholderProps {
  lastUserMessage: ChatMessage;
  modeChips: ModeChip[];
  activeMode?: string;
  onModeChange?: (modeId: string) => void;
  tabs: TabItem[];
  activeTab?: string;
  lastResponsePairId: string | null;
  getActiveTabForConversation: (conversationId: string) => string;
  onTabChange?: (tabId: string) => void;
  handleConversationTabChange: (
    tabId: string,
    conversationId: string,
    isLastPair: boolean
  ) => void;
  getIcon: (iconName?: string) => React.ReactNode;
  isLoading?: boolean;
  isLoadingResponse?: boolean;
  isLoadingFollowUps?: boolean;
  isLoadingExperiences?: boolean;
  messages: ChatMessage[];
}

export const LoadingResponsePlaceholder: React.FC<
  LoadingResponsePlaceholderProps
> = ({
  lastUserMessage,
  modeChips,
  activeMode,
  onModeChange,
  tabs,
  activeTab,
  lastResponsePairId,
  getActiveTabForConversation,
  onTabChange,
  handleConversationTabChange,
  getIcon,
  isLoading,
  isLoadingResponse = false,
  isLoadingFollowUps = false,
  isLoadingExperiences = false,
  messages,
}) => {
  // Determine which loading phase we're in
  const getLoadingPhase = (): 'thinking' | 'collecting' | 'structuring' => {
    if (isLoadingResponse) return 'thinking';
    if (isLoadingFollowUps) return 'collecting';
    if (isLoadingExperiences) return 'structuring';
    return 'thinking'; // Default
  };

  const loadingPhase = getLoadingPhase();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[8],
      }}
    >
      {/* User Prompt Text (from the last user message) */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
        }}
      >
        {/* Mode Chips */}
        <div
          style={{
            display: 'flex',
            gap: spacing[2],
          }}
        >
          {modeChips.map(chip => (
            <Chip
              key={chip.id}
              label={chip.label}
              icon={getIcon(chip.icon)}
              isActive={activeMode === chip.id}
              onClick={() => onModeChange?.(chip.id)}
            />
          ))}
        </div>

        {/* User Prompt Text */}
        <div
          style={{
            display: 'flex',
            gap: spacing[2],
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: spacing[0],
            paddingRight: spacing[0],
            paddingTop: spacing[2],
            paddingBottom: spacing[2],
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              flex: '1 0 0',
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[3],
            }}
          >
            <ExpandableText
              text={lastUserMessage.content}
              maxLines={5}
              showMoreText='Show more'
              showLessText='Show less'
              style={{
                fontFamily: fontPrimary,
                fontWeight: typography.weight.semibold,
                fontSize: typography.size['2xl'],
                lineHeight: 'normal',
                color: typographyColors.primary,
                margin: 0,
                whiteSpace: 'pre-wrap',
              }}
            />

            {/* Transcript for audio messages */}
            {lastUserMessage.transcription && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[1],
                }}
              >
                <span
                  style={{
                    fontFamily: fontPrimary,
                    fontWeight: typography.weight.medium,
                    fontSize: typography.size.sm,
                    color: typographyColors.secondary,
                  }}
                >
                  Transcript
                </span>
                <ExpandableText
                  text={lastUserMessage.transcription}
                  maxLines={5}
                  showMoreText='Show more'
                  showLessText='Show less'
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.base,
                    lineHeight: 'normal',
                    color: typographyColors.primary,
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                  }}
                />
              </div>
            )}

            {/* Audio Player for audio messages */}
            {lastUserMessage.audioBase64 && (
              <AudioPlayer audioBase64={lastUserMessage.audioBase64} />
            )}
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: spacing[3],
          }}
        >
          {tabs
            .filter(tab => {
              // Hide places tab if there are no places in the latest message
              if (tab.id === 'places') {
                const latestMessage = messages
                  .slice()
                  .reverse()
                  .find(
                    msg =>
                      msg.role === 'assistant' &&
                      msg.locations &&
                      msg.locations.length > 0
                  );
                if (!latestMessage?.locations) {
                  return false;
                }
                const hasValidLocations = latestMessage.locations.some(
                  location =>
                    location.place_id &&
                    (location.title || location.place || location.place_name)
                );
                return hasValidLocations;
              }
              return true;
            })
            .map(tab => {
              // For loading placeholder (current chat), use the last response pair's tab state
              // or fallback to global activeTab if not yet set
              const currentChatTab = lastResponsePairId
                ? getActiveTabForConversation(lastResponsePairId)
                : activeTab || tabs[0]?.id || '';
              const isActive = currentChatTab === tab.id;

              return (
                <Tab
                  key={tab.id}
                  label={tab.label}
                  isActive={isActive}
                  onClick={() => {
                    // Update current chat's tab state
                    if (lastResponsePairId) {
                      handleConversationTabChange(
                        tab.id,
                        lastResponsePairId,
                        true // isLastPair = true for current chat
                      );
                    } else {
                      // Fallback: call onTabChange directly if no lastResponsePairId
                      onTabChange?.(tab.id);
                    }
                  }}
                  disabled={isLoading}
                />
              );
            })}
        </div>

        {/* Divider */}
        <div
          style={{
            width: '100%',
            height: '1px',
            backgroundColor: strokeColors.base,
          }}
        />
      </div>

      {/* Loading Placeholder with phase indicator */}
      <ChatLoadingPlaceholder loadingPhase={loadingPhase} />
    </div>
  );
};
